(ns app.front.dashboard.service-widget
  "Dashboard service widget that replaces the old create-preliminary-selections.
   Shows a NoVa AI assistant intro panel with NoVa input, New Application,
   Local Services buttons, and CTA chips from the municipality's landing config."
  (:require ["@carbon/icons-react/es/information/24" :default Information24]
            [app.domain.branding.model :as branding-model]
            [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.call-to-action :as call-to-action]
            [app.front.ui.cards :as cards]
            [app.front.ui.chat-input :as chat-input]
            [app.front.ui.chat-panel :as chat-panel]
            [app.front.ui.panels :as panels]
            [app.front.util :as futil]
            [app.front.view.create-application.create-v2 :as create-v2]
            [app.front.view.landing-page :as landing-page]
            [app.front.view.navigation :as navigation]
            [core.front.carbon-icons :as carbon-icons]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;;
;; State
;;

(rf/reg-sub ::mode
  (fn [db _]
    (or (::mode db) :default)))

;;
;; Landing config for dashboard
;;

(rf/reg-sub ::landing-config
  :-> ::landing-config)

(rf/reg-sub ::landing-config-loaded?
  :<- [::landing-config]
  :-> some?)

(rf/reg-event-fx ::init
  (fn [{:keys [db]} _]
    (let [branding (:branding db)]
      (when (and branding
                 (not= branding branding-model/default-branding))
        {:db (dissoc db ::landing-config)
         :fx [[:dispatch [:comm/command-monitored
                          {:command    :group/get-landing-config
                           :method     :get
                           :data       {:brand branding}
                           :on-success [::init.success]}]]]}))))

(rf/reg-event-db ::init.success
  (fn [db [_ _ config]]
    (cond-> (assoc db
                   ::landing-config config
                   ;; Mirror calls-to-action so the CTA side panel reads them correctly
                   ;; (call_to_action.cljs reads from :app.front.view.landing-page/landing-config)
                   ::landing-page/landing-config
                   (select-keys config [:calls-to-action]))
      (-> config :landing :entities)
      (entities/merge-entity-db (-> config :landing :entities)))))

;;
;; Municipality context
;;

(rf/reg-sub ::has-municipality-context?
  :<- [::landing-config-loaded?]
  :<- [::group-type]
  (fn [[loaded? group-type] _]
    (and loaded? (not= group-type :group.type/superdomain))))

(rf/reg-sub ::group-type
  :<- [::landing-config]
  :-> :group-type)

(rf/reg-sub ::domain-group
  :<- [::landing-config]
  (fn [landing-config _]
    (->> landing-config
         :landing
         :entities
         vals
         (filter group-model/domain?)
         first)))

;;
;; Permit type configs from landing config entities
;;

(rf/reg-sub ::permit-type-configs
  :<- [::entities/context]
  :<- [::landing-config-loaded?]
  :<- [:branding]
  (fn [[ctx loaded? brand-slug] _]
    (when loaded?
      (->> (entities/find-entity ctx {:group/uri-path-id brand-slug})
           group-model/descendant-authority-groups
           (mapcat group-model/permit-type-configs)))))

(rf/reg-sub ::first-municipality
  :<- [::permit-type-configs]
  (fn [ptcs _]
    (some-> ptcs first landing-page/permit-type-config-municipality)))

;;
;; Other services card data (Local Services)
;;

(rf/reg-sub ::public-records-card-data
  :<- [:tr]
  :<- [::landing-config]
  (fn [[tr {:keys [public-records-tabs]}] _]
    (when (seq public-records-tabs)
      (landing-page/public-records-card-data tr public-records-tabs))))

(rf/reg-sub ::complaint-card-data
  :<- [:tr]
  :<- [::landing-config]
  :<- [:language]
  (fn [[tr {:keys [show-complaints? complaint-submission? complaint-display-name]} lang] _]
    (when (and show-complaints? complaint-submission?)
      (landing-page/complaints-card-data tr nil complaint-display-name lang))))

(rf/reg-sub ::contact-request-card-data
  :<- [:tr]
  :<- [::landing-config]
  :<- [::domain-group]
  (fn [[tr landing-config domain-group] _]
    (when (:contact-request? landing-config)
      (landing-page/contact-request-card-data tr (:group/id domain-group)))))

(rf/reg-sub ::other-services-cards
  :<- [::public-records-card-data]
  :<- [::complaint-card-data]
  :<- [::contact-request-card-data]
  (fn [[pr c cr] _]
    (vec (concat pr c cr))))

;;
;; CTA items
;;

(rf/reg-sub ::cta-items
  :<- [::landing-config]
  :-> :calls-to-action)

;;
;; Events
;;

(rf/reg-event-fx ::toggle-new-application
  (fn [{:keys [db]} [_ {:municipality/keys [number subdivision]}]]
    (let [current-mode        (or (::mode db) :default)
          application-wizard? (ff/enabled? :feature-flag.id/application-wizard)]
      (if (= current-mode :new-application)
        {:db (assoc db ::mode :default)
         :fx [[:dispatch [::create-v2/close-preliminary-selections]]]}
        {:db (assoc db ::mode :new-application)
         :fx [;; Pass branding municipality so ::start-create pre-seeds the selection via ::initialize-view.
              ;; ::municipalities-loaded then picks it up and triggers ::set-subdiv-and-municipality.
              [:dispatch [(if application-wizard?
                            ::create-v2/open-preliminary-selections
                            ::create-v2/start-create)
                          number subdivision]]
              [:dispatch [::navigation/scroll-to-top]]]}))))

(rf/reg-event-fx ::toggle-local-services
  (fn [{:keys [db]} _]
    (let [current-mode (or (::mode db) :default)]
      {:db (assoc db ::mode (if (= current-mode :local-services)
                              :default
                              :local-services))})))

(rf/reg-event-fx ::submit-nova
  (fn [_ [_ message]]
    (when (seq message)
      {:fx [[:dispatch [::panels/open :chat]]
            [:dispatch [::chat-panel/send-message message]]]})))

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} _]
    {:db (-> db
             (dissoc ::mode)
             (dissoc ::landing-config)
             (dissoc :app.front.view.landing-page/landing-config))
     :fx [[:dispatch [::create-v2/close-preliminary-selections]]]}))

;;
;; Components
;;

(defn- nova-input [tr]
  [chat-input/chat-input
   {:id               "nova-input"
    :input-class      "p-12 pl-6 py-6 border border-solid border-gray-300 rounded-full bg-white"
    :placeholder      (tr [:service-widget/ask-nova-placeholder])
    :label-text       (tr [:service-widget/ask-nova-placeholder])
    :send-description (tr [:chat/send])
    :on-submit        #(>evt [::submit-nova %])}])

(defn- cta-item
  "A small bordered CTA item (e.g. City Hall Hours).
   Icon is a Carbon React component rendered via [:>]."
  [{:keys [title subtitle icon on-click]}]
  [:button.flex.items-center.gap-2.border.border-solid.border-gray-300.rounded.px-3.py-2.bg-white.cursor-pointer.hover:bg-gray-50.text-left.w-full.sm:w-auto
   {:on-click on-click}
   (when icon
     [:> icon {:size 16}])
   [:div.w-full.flex.justify-between.sm:w-auto.sm:flex-col.sm:justify-normal
    [:span.text-xs.font-medium title]
    (when subtitle
      [:span.text-xs.text-gray-600 subtitle])]])

(defn- cta-chips []
  (let [lang            (<sub [:language])
        calls-to-action (<sub [::cta-items])]
    (when (seq calls-to-action)
      [:div.flex.flex-col.flex-wrap.gap-2.w-full.sm:flex-row.sm:w-auto
       (futil/for-frag [[idx cta] (map-indexed vector calls-to-action)]
         [cta-item {:title    (call-to-action/call-to-action-title cta lang)
                    :subtitle (call-to-action/call-to-action-subtitle cta lang)
                    :icon     (get carbon-icons/icons (:call-to-action/icon cta) Information24)
                    :on-click #(>evt [::call-to-action/toggle-open idx])}])])))

(defn- action-buttons
  "The NEW APPLICATION and LOCAL SERVICES toggle buttons."
  [tr]
  (let [mode       (<sub [::mode])
        has-muni?  (<sub [::has-municipality-context?])
        first-muni (<sub [::first-municipality])]
    [:<>
     [ui/button {:kind       :primary
                 :icon       (when (= mode :new-application) i/remove)
                 :on-click   #(>evt [::toggle-new-application first-muni])
                 :size       :default
                 :class-name "flex-1 min-w-0 w-full max-w-full justify-center"}
      (if (= mode :new-application)
        (tr [:cancel])
        (tr [:application/new]))]
     (when has-muni?
       [ui/button {:kind       (if (= mode :local-services) :primary :secondary)
                   :icon       (when (= mode :local-services) i/minus)
                   :on-click   #(>evt [::toggle-local-services])
                   :size       :default
                   :class-name "flex-1 min-w-0 w-full max-w-full justify-center"}
        (if (= mode :local-services)
          (tr [:service-widget/hide-services])
          (tr [:service-widget/local-services]))])]))

(defn- local-services-content
  "Grid of local services cards (Public Records, File a Complaint, Contact Request)."
  []
  (let [other-cards (<sub [::other-services-cards])]
    (when (seq other-cards)
      [cards/landing-cards {:cards other-cards}])))


(defn service-widget
  "Main dashboard service widget. Shows the NoVa AI assistant intro with
   NoVa input, New Application / Local Services buttons, and CTA chips."
  [tr]
  (r/with-let [_ (>evt [::init])]
    (let [mode                   (<sub [::mode])
          has-muni?              (<sub [::has-municipality-context?])
          no-branding?           (nil? (<sub [:branding]))
          landing-config-loaded? (<sub [::landing-config-loaded?])
          domain-group           (<sub [::domain-group])
          ai-chat?               (and (ff/enabled? :feature-flag.id/ai-chat)
                                      (or landing-config-loaded? no-branding?)
                                      (not (group-model/ai-disabled? domain-group)))
          user                   (<sub [:session/user])]
      [:section.dashlet.p-6 {:data-cy "service-widget"}
       ;; Header row
       [:div.flex.flex-col.gap-4
        ;; Title + CTA chips row
        (when (or ai-chat? has-muni?)
          [:div.flex.flex-col.gap-3.sm:flex-row.sm:items-start.sm:flex-wrap
           (when ai-chat?
             [:h2.m-0.text-xl.font-bold (tr [:service-widget/heading])])
           (when has-muni?
             [:div.sm:ml-auto
              [cta-chips]])])
        ;; NoVa input + action buttons row — share the row equally
        [:div.flex.flex-col.gap-3.sm:flex-row.sm:items-center
         (when (and ai-chat? user)
           [:div.min-w-0 {:class "flex-[1]"}
            [nova-input tr]])
         [:div.flex.gap-3.w-full.sm:w-auto {:class "flex-[1]"}
          [action-buttons tr]]]]
       ;; Content area (below divider)
       (when (not= mode :default)
         [:<>
          [:hr.border-gray-200.my-6]
          (case mode
            :new-application  [create-v2/create-preliminary-selections tr]
            :local-services   [local-services-content]
            nil)])])))
